import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Upload, message, Typography, Alert, Spin, Progress, Table, Select, DatePicker, Space, Button,
} from 'antd';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import api from '../services/api';
import { canEdit } from '../utils/auth';
import { FILE_STATUS_COLORS, palette } from '../theme';
import Pill from '../components/Pill';

const { Dragger } = Upload;

// Санкционный список сознательно НЕ пытаемся распознать из содержимого
// файла — разные режимы форматируют это по-разному или вообще не выделяют
// как отдельное поле, надёжный автоматический разбор невозможен. Пользователь
// указывает источник явно, значение применяется ко всем записям файла и
// используется дедупликацией, чтобы не путать одну и ту же компанию,
// попавшую под санкции по независимым режимам.
// "Украина" убрана из выбора по требованию — загрузка украинского списка
// больше не поддерживается.
// "РФ" и "Секторальные санкции" — два независимых друг от друга источника
// (не вложенные один в другой, у каждого свой фиксированный номер списка,
// см. FileProcessingService.fixedListNumbers на бэкенде).
const SANCTION_LIST_OPTIONS = [
  { label: 'США (US)', value: 'US' },
  { label: 'Великобритания (UK)', value: 'UK' },
  { label: 'Евросоюз (EU)', value: 'EU' },
  { label: 'ООН (UN)', value: 'UN' },
  { label: 'Россия (РФ)', value: 'RU' },
  { label: 'Секторальные санкции', value: 'SECTOR' },
];

const UploadPage: React.FC = () => {
  const editable = canEdit();
  const [uploading, setUploading] = useState(false);
  const [lastFile, setLastFile] = useState<any>(null);
  const [fileStatus, setFileStatus] = useState<string>('');
  const [lastProgress, setLastProgress] = useState<number>(0);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPageSize, setHistoryPageSize] = useState(20);
  const [historyPage, setHistoryPage] = useState(1);
  const [sanctionList, setSanctionList] = useState<string | undefined>(undefined);
  const [sanctionDate, setSanctionDate] = useState<Dayjs | null>(null);

  const fetchHistory = useCallback(() => {
    setHistoryLoading(true);
    api.get('/files/')
      .then(r => setHistory(r.data))
      .catch(() => message.error('Ошибка загрузки истории'))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    if (!lastFile?.id) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/files/${lastFile.id}`);
        setFileStatus(res.data.status);
        setLastProgress(res.data.progress || 0);
        if (res.data.status === 'parsed') {
          message.success(`✅ Обработано: ${res.data.entities_count} ЮЛ`);
          clearInterval(interval);
          fetchHistory();
        } else if (res.data.status === 'error') {
          message.error(`❌ ${res.data.error_message}`);
          clearInterval(interval);
          fetchHistory();
        }
      } catch {
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [lastFile, fetchHistory]);

  const handleUpload = async (file: File) => {
    if (!sanctionList || !sanctionDate) {
      message.error('Сначала выберите санкционный список и дату внесения в список');
      return false;
    }
    setUploading(true);
    setFileStatus('');
    setLastProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sanction_list', sanctionList);
    formData.append('sanction_date', sanctionDate.format('YYYY-MM-DD'));
    try {
      // Реальный случай (NMiller, Edge, 2026-08-18 13:12): жёстко заданный
      // здесь заголовок "multipart/form-data" БЕЗ boundary заставлял Edge
      // отправлять запрос без разделителя частей формы — сервер получал
      // тело, в котором поля file/sanction_list/sanction_date невозможно
      // было разобрать (MissingServletRequestParameterException:
      // sanction_list). Chrome в тех же условиях молча переопределял
      // Content-Type сам, поэтому баг не проявлялся там. Правильно —
      // ВООБЩЕ не задавать Content-Type для FormData: axios/браузер сами
      // подставляют корректный "multipart/form-data; boundary=...".
      const res = await api.post('/files/upload', formData);
      setLastFile(res.data);
      message.success('Файл загружен, парсинг запущен');
      fetchHistory();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка загрузки');
    }
    setUploading(false);
    return false;
  };

  // Исходный файл хранится на сервере бессрочно (не удаляется после
  // разбора) — скачиваем его как Blob (не простой <a href>), т.к.
  // аутентификация здесь через Bearer-токен в заголовке, а не куки,
  // обычная ссылка его не понесёт. Имя файла берём из самой строки
  // таблицы (record.filename) — оно уже известно, не нужно разбирать
  // Content-Disposition, как при экспорте Excel (там имя генерируется
  // сервером на лету).
  const handleDownload = async (record: any) => {
    try {
      const res = await api.get(`/files/${record.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = record.filename || `file_${record.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка скачивания файла');
    }
  };

  const columns: any[] = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Файл', dataIndex: 'filename', ellipsis: true },
    { title: 'Источник', dataIndex: 'source_type', width: 140 },
    {
      title: 'Статус', dataIndex: 'status', width: 140,
      render: (s: string) => {
        const info = FILE_STATUS_COLORS[s] || { bg: palette.border, fg: palette.muted, label: s };
        return <Pill bg={info.bg} fg={info.fg}>{info.label}</Pill>;
      },
    },
    { title: 'ЮЛ найдено', dataIndex: 'entities_count', width: 110 },
    {
      title: 'Загружен', dataIndex: 'uploaded_at', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('ru-RU') : '—',
    },
    { title: 'Ошибка', dataIndex: 'error_message', ellipsis: true },
    {
      title: '', key: 'download', width: 50,
      render: (_: any, record: any) => (
        <Button type="text" icon={<DownloadOutlined />} title="Скачать файл" onClick={() => handleDownload(record)} />
      ),
    },
  ];

  return (
    <div>
      <div className="eyebrow">Источники данных</div>
      <Typography.Title level={3} className="page-title" style={{ marginTop: 0 }}>Загрузка файлов</Typography.Title>
      {editable && (
        <Card style={{ marginBottom: 16 }}>
          <Space style={{ marginBottom: 16 }} wrap>
            <Select placeholder="Санкционный список" style={{ width: 220 }}
              value={sanctionList}
              onChange={setSanctionList}
              options={SANCTION_LIST_OPTIONS} />
            <DatePicker placeholder="Дата внесения в список" format="DD.MM.YYYY"
              value={sanctionDate}
              onChange={setSanctionDate} />
          </Space>
          {(!sanctionList || !sanctionDate) && (
            <Alert
              message="Перед загрузкой укажите санкционный список и дату внесения — это не парсится из документа, эти значения применятся ко всем записям файла."
              type="info" showIcon style={{ marginBottom: 16 }}
            />
          )}
          <Dragger accept=".xlsx,.xls,.pdf" multiple={false}
            disabled={!sanctionList || !sanctionDate}
            beforeUpload={handleUpload} showUploadList={false}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Нажмите или перетащите файл сюда</p>
            <p className="ant-upload-hint">Поддерживаются .xlsx, .xls, .pdf</p>
          </Dragger>
          {uploading && <Spin tip="Загрузка..." style={{ marginTop: 12 }} />}
          {fileStatus === 'parsing' && lastProgress > 0 && (
            <Progress percent={lastProgress} status="active" style={{ marginTop: 12 }} />
          )}
          {fileStatus === 'parsed' && (
            <Alert message="✅ Обработка завершена" type="success" showIcon style={{ marginTop: 12 }} />
          )}
          {fileStatus === 'error' && (
            <Alert message="❌ Ошибка обработки" type="error" showIcon style={{ marginTop: 12 }} />
          )}
        </Card>
      )}

      <Typography.Title level={5} className="page-title">История загрузок</Typography.Title>
      <Table
        columns={columns}
        dataSource={history}
        rowKey="id"
        loading={historyLoading}
        pagination={{
          current: historyPage,
          pageSize: historyPageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            setHistoryPage(page);
            if (size !== historyPageSize) setHistoryPageSize(size);
          },
        }}
        size="small"
      />
    </div>
  );
};

export default UploadPage;